/**
 * FEAT-012 — Per-transaction properties panel.
 *
 * Surfaces the live metrics for one in-flight transaction:
 *   - transactionId (with copy button)
 *   - startedAt (absolute + relative)
 *   - elapsed (s / m / h, refreshed once per second)
 *   - delivered kWh (running)
 *   - instantaneous kW
 *   - SoC % (DC only; reads from connector.currentSocPct)
 *
 * `startedAt` and `kWh` are not held in `useStationStore` for in-flight
 * transactions, so the component reads them from Dexie when the tx changes
 * and caches them locally. The 1-Hz refresh that drives elapsed uses the
 * shared `useInterval` hook, so the cell visibly ticks.
 */
import { useEffect, useState } from 'react';
import type { ConnectorId } from '@/state/types';
import type { TransactionSnapshot } from '@/state/types';
import { useStationStore } from '@/state/useStationStore';
import { listTransactionsByStation } from '@/state/repos';
import { useInterval } from '@/hooks/useInterval';
import { CopyButton } from './CopyButton';
import { formatTime } from '@/lib/format';

function inferDc(evseName: string | undefined): boolean {
  return evseName !== undefined && /\bdc\b/i.test(evseName);
}

function formatElapsed(ms: number): string {
  if (ms < 0) return '0s';
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ${sec % 60}s`;
  const h = Math.floor(min / 60);
  return `${h}h ${min % 60}m`;
}

function formatRelative(at: number, now: number): string {
  const ms = now - at;
  if (ms < 60_000) return 'just now';
  const min = Math.floor(ms / 60_000);
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export function TransactionPropertiesPanel({
  connectorId,
}: {
  connectorId: ConnectorId;
}): JSX.Element | null {
  const connector = useStationStore((s) => s.connectors.get(connectorId));
  const stationId = useStationStore((s) => s.stationId);
  const evseName = useStationStore((s) =>
    s.evses.find((e) => e.id === connector?.evseId)?.name,
  );

  const [tx, setTx] = useState<TransactionSnapshot | null>(null);
  const [now, setNow] = useState<number>(() => Date.now());

  const txId = connector?.activeTransactionId ?? null;

  useEffect(() => {
    let cancelled = false;
    if (txId === null) {
      setTx(null);
      return undefined;
    }
    void (async (): Promise<void> => {
      const rows = await listTransactionsByStation(stationId);
      if (cancelled) return;
      const match = rows.find((r) => r.id === txId) ?? null;
      setTx(match);
    })();
    return () => {
      cancelled = true;
    };
  }, [stationId, txId]);

  useInterval(() => setNow(Date.now()), 1000);

  if (!connector || txId === null) return null;

  const startedAt = tx?.startedAt ?? null;
  const elapsedMs = startedAt !== null ? now - startedAt : 0;
  // kWh delivered ≈ (avg power kW × hours). Using the instantaneous power
  // is an underestimate when load is variable, but matches the in-store
  // `effectivePowerW` we already display elsewhere — consistent for QA.
  const elapsedH = elapsedMs / 1000 / 3600;
  const kwhDelivered = (connector.effectivePowerW / 1000) * elapsedH;
  const isDc = inferDc(evseName);

  return (
    <section
      className="rounded border border-slate-700 bg-slate-900/60 p-3 text-xs"
      data-testid={`tx-panel-${connectorId}`}
      aria-label={`Transaction details for connector ${connectorId}`}
    >
      <header className="mb-2 flex items-center justify-between">
        <span className="font-medium text-slate-100">
          Connector {String(connectorId)} · tx#{String(txId)}
        </span>
        <CopyButton
          value={String(txId)}
          label="Copy txId"
          testId={`tx-panel-${connectorId}-copy`}
        />
      </header>
      <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-slate-300">
        <dt className="text-slate-500">Started</dt>
        <dd data-testid={`tx-panel-${connectorId}-started`}>
          {startedAt !== null ? (
            <>
              <time dateTime={new Date(startedAt).toISOString()}>
                {formatTime(startedAt)}
              </time>
              <span className="ml-1 text-slate-500">
                ({formatRelative(startedAt, now)})
              </span>
            </>
          ) : (
            <span className="text-slate-500">—</span>
          )}
        </dd>
        <dt className="text-slate-500">Elapsed</dt>
        <dd
          className="font-mono"
          data-testid={`tx-panel-${connectorId}-elapsed`}
        >
          {startedAt !== null ? formatElapsed(elapsedMs) : '—'}
        </dd>
        <dt className="text-slate-500">Power</dt>
        <dd className="font-mono" data-testid={`tx-panel-${connectorId}-kw`}>
          {(connector.effectivePowerW / 1000).toFixed(2)} kW
        </dd>
        <dt className="text-slate-500">Delivered</dt>
        <dd className="font-mono" data-testid={`tx-panel-${connectorId}-kwh`}>
          {kwhDelivered.toFixed(3)} kWh
        </dd>
        {isDc ? (
          <>
            <dt className="text-slate-500">SoC</dt>
            <dd className="font-mono" data-testid={`tx-panel-${connectorId}-soc`}>
              {connector.currentSocPct !== null
                ? `${connector.currentSocPct.toFixed(1)}%`
                : '—'}
            </dd>
          </>
        ) : null}
        <dt className="text-slate-500">idTag</dt>
        <dd className="font-mono" data-testid={`tx-panel-${connectorId}-idtag`}>
          {connector.activeIdTag ?? '—'}
        </dd>
      </dl>
    </section>
  );
}
